// @ts-check
// Leitor mínimo de XLSX (planilha do Excel) para a importação: abre o zip com DecompressionStream
// ('deflate-raw', presente nos navegadores atuais e no Workers) e lê os XML das abas por expressões regulares.
// Sem dependência externa: o pacote do SheetJS no npm está desatualizado e o resto do formato não é usado.
// Datas viram texto ISO (AAAA-MM-DD ou AAAA-MM-DD HH:MM) e números usam vírgula decimal, como digitados no Excel pt-BR.

const END_OF_CENTRAL_DIRECTORY_SIGNATURE = 0x06054b50;
const CENTRAL_DIRECTORY_SIGNATURE = 0x02014b50;
const LOCAL_FILE_HEADER_SIGNATURE = 0x04034b50;
const MAXIMUM_COMMENT_LENGTH = 0xffff;
const STORED = 0;
const DEFLATED = 8;
const MAXIMUM_COLUMNS = 300;
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000;
// formatos de data embutidos no Excel (ECMA-376, 18.8.30)
const BUILT_IN_DATE_FORMAT_IDS = new Set([14, 15, 16, 17, 18, 19, 20, 21, 22, 27, 30, 36, 45, 46, 47, 50, 57]);

export class SpreadsheetReadError extends Error {
  /** @param {string} code @param {string} message */
  constructor(code, message) {
    super(message);
    this.name = 'SpreadsheetReadError';
    this.code = code;
  }
}

/**
 * @typedef {{ name: string, rows: string[][] }} SpreadsheetSheet
 */

/** @param {ArrayBuffer | Uint8Array} input */
const toBytes = (input) => (input instanceof Uint8Array ? input : new Uint8Array(input));

/**
 * Indica se os bytes começam com a assinatura de um zip (XLSX); CSV e TSV não começam.
 * @param {ArrayBuffer | Uint8Array} input
 */
export function looksLikeZip(input) {
  const bytes = toBytes(input);
  return bytes.length >= 4 && bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04;
}

/**
 * Entradas do zip pelo diretório central: nome, método, tamanhos e posição do cabeçalho local.
 * @param {Uint8Array} bytes
 */
function readZipEntries(bytes) {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const searchStart = Math.max(0, bytes.length - 22 - MAXIMUM_COMMENT_LENGTH);
  let endOffset = -1;
  for (let offset = bytes.length - 22; offset >= searchStart; offset -= 1) {
    if (view.getUint32(offset, true) === END_OF_CENTRAL_DIRECTORY_SIGNATURE) {
      endOffset = offset;
      break;
    }
  }
  if (endOffset === -1) throw new SpreadsheetReadError('INVALID_SPREADSHEET', 'The file is not a valid XLSX (zip directory not found)');
  const entryCount = view.getUint16(endOffset + 10, true);
  let offset = view.getUint32(endOffset + 16, true);
  const decoder = new TextDecoder();
  /** @type {Map<string, { method: number, compressedSize: number, localHeaderOffset: number }>} */
  const entries = new Map();
  for (let index = 0; index < entryCount; index += 1) {
    if (view.getUint32(offset, true) !== CENTRAL_DIRECTORY_SIGNATURE) {
      throw new SpreadsheetReadError('INVALID_SPREADSHEET', 'The file is not a valid XLSX (corrupted zip directory)');
    }
    const method = view.getUint16(offset + 10, true);
    const compressedSize = view.getUint32(offset + 20, true);
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    const localHeaderOffset = view.getUint32(offset + 42, true);
    const name = decoder.decode(bytes.subarray(offset + 46, offset + 46 + nameLength));
    entries.set(name.replace(/^\/+/, ''), { method, compressedSize, localHeaderOffset });
    offset += 46 + nameLength + extraLength + commentLength;
  }
  return { view, entries };
}

/**
 * @param {Uint8Array} data
 */
async function inflateRaw(data) {
  const stream = new Blob([/** @type {Uint8Array<ArrayBuffer>} */ (data)]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new Uint8Array(await new Response(stream).arrayBuffer());
}

/**
 * Texto de um arquivo do zip, ou null quando ele não existe.
 * @param {Uint8Array} bytes @param {ReturnType<typeof readZipEntries>} zip @param {string} path
 */
async function readZipText(bytes, zip, path) {
  const entry = zip.entries.get(path);
  if (!entry) return null;
  const { view } = zip;
  const headerOffset = entry.localHeaderOffset;
  if (view.getUint32(headerOffset, true) !== LOCAL_FILE_HEADER_SIGNATURE) {
    throw new SpreadsheetReadError('INVALID_SPREADSHEET', `The file is not a valid XLSX (bad header for ${path})`);
  }
  const nameLength = view.getUint16(headerOffset + 26, true);
  const extraLength = view.getUint16(headerOffset + 28, true);
  const dataStart = headerOffset + 30 + nameLength + extraLength;
  const data = bytes.subarray(dataStart, dataStart + entry.compressedSize);
  let content;
  if (entry.method === STORED) content = data;
  else if (entry.method === DEFLATED) content = await inflateRaw(data);
  else throw new SpreadsheetReadError('UNSUPPORTED_SPREADSHEET', `Unsupported zip compression method ${entry.method}`);
  // prefixos de namespace (<x:c>, <x:row>) aparecem em planilhas geradas por outros programas
  return new TextDecoder().decode(content).replace(/<(\/?)[A-Za-z_][\w.-]*:(?=[A-Za-z_])/g, '<$1');
}

/** @param {string} text */
export function decodeXmlText(text) {
  return text
    .replace(/&(lt|gt|amp|quot|apos|#\d+|#x[0-9a-fA-F]+);/g, (_match, entity) => {
      switch (entity) {
        case 'lt': return '<';
        case 'gt': return '>';
        case 'amp': return '&';
        case 'quot': return '"';
        case 'apos': return "'";
        default:
          return String.fromCodePoint(entity[1] === 'x' ? parseInt(entity.slice(2), 16) : parseInt(entity.slice(1), 10));
      }
    })
    // escape do OOXML para caracteres de controle (_x000D_)
    .replace(/_x([0-9a-fA-F]{4})_/g, (_match, code) => String.fromCharCode(parseInt(code, 16)));
}

/** @param {string} tagText atributos de uma tag */
function readAttributes(tagText) {
  /** @type {Record<string, string>} */
  const attributes = {};
  for (const match of tagText.matchAll(/([\w:.-]+)\s*=\s*(?:"([^"]*)"|'([^']*)')/g)) {
    attributes[match[1]] = decodeXmlText(match[2] ?? match[3] ?? '');
  }
  return attributes;
}

/** Texto de um <si> ou <is>: junta os <t>, sem a fonética (<rPh>). @param {string} xml */
function readRichText(xml) {
  const withoutPhonetic = xml.replace(/<rPh\b[\s\S]*?<\/rPh>/g, '');
  let text = '';
  for (const match of withoutPhonetic.matchAll(/<t\b[^>]*?(?:\/>|>([\s\S]*?)<\/t>)/g)) text += decodeXmlText(match[1] ?? '');
  return text;
}

/** @param {string | null} xml */
function readSharedStrings(xml) {
  if (!xml) return [];
  return [...xml.matchAll(/<si\b[^>]*?(?:\/>|>([\s\S]*?)<\/si>)/g)].map((match) => readRichText(match[1] ?? ''));
}

/** Formato de data quando há d, m, a, h ou s fora de aspas e colchetes de cor. @param {string} formatCode */
function isDateFormatCode(formatCode) {
  const cleaned = formatCode.replace(/"[^"]*"/g, '').replace(/\\./g, '').replace(/\[(?!h\]|m\]|s\])[^\]]*\]/gi, '');
  if (/^general$/i.test(cleaned.trim())) return false;
  return /[dmyhs]/i.test(cleaned);
}

/**
 * Índices de estilo (atributo s da célula) que formatam data.
 * @param {string | null} xml
 * @returns {Set<number>}
 */
function readDateStyleIndexes(xml) {
  /** @type {Set<number>} */
  const indexes = new Set();
  if (!xml) return indexes;
  /** @type {Map<number, string>} */
  const customFormats = new Map();
  for (const match of xml.matchAll(/<numFmt\b([^>]*)\/?>/g)) {
    const attributes = readAttributes(match[1]);
    customFormats.set(Number(attributes.numFmtId), attributes.formatCode ?? '');
  }
  const cellFormats = /<cellXfs\b[^>]*>([\s\S]*?)<\/cellXfs>/.exec(xml)?.[1] ?? '';
  let index = 0;
  for (const match of cellFormats.matchAll(/<xf\b([^>]*?)(?:\/>|>[\s\S]*?<\/xf>)/g)) {
    const formatId = Number(readAttributes(match[1]).numFmtId ?? 0);
    const customFormat = customFormats.get(formatId);
    if (BUILT_IN_DATE_FORMAT_IDS.has(formatId) || (customFormat !== undefined && isDateFormatCode(customFormat))) indexes.add(index);
    index += 1;
  }
  return indexes;
}

/** "BC12" -> 54 (base zero). @param {string} reference */
function columnIndexFromReference(reference) {
  const letters = /^[A-Z]+/i.exec(reference)?.[0]?.toUpperCase() ?? '';
  let index = 0;
  for (const letter of letters) index = index * 26 + (letter.charCodeAt(0) - 64);
  return index - 1;
}

/**
 * Número de série do Excel em data ISO; com fração de dia, inclui a hora.
 * @param {number} serial @param {boolean} isDate1904
 */
export function excelSerialToIsoText(serial, isDate1904 = false) {
  const epoch = isDate1904 ? Date.UTC(1904, 0, 1) : Date.UTC(1899, 11, 30);
  const milliseconds = Math.round((serial * MILLISECONDS_PER_DAY) / 1000) * 1000;
  const iso = new Date(epoch + milliseconds).toISOString();
  return Number.isInteger(serial) || iso.slice(11, 16) === '00:00' ? iso.slice(0, 10) : `${iso.slice(0, 10)} ${iso.slice(11, 16)}`;
}

/** Número como o Excel pt-BR mostraria ao digitar: vírgula decimal, sem milhar. @param {string} raw */
function formatNumberCell(raw) {
  const numberValue = Number(raw);
  if (!Number.isFinite(numberValue)) return raw;
  return String(numberValue).replace('.', ',');
}

/**
 * @param {string} xml @param {string[]} sharedStrings @param {Set<number>} dateStyleIndexes @param {boolean} isDate1904
 * @returns {string[][]}
 */
function readSheetRows(xml, sharedStrings, dateStyleIndexes, isDate1904) {
  const sheetData = /<sheetData\b[^>]*?(?:\/>|>([\s\S]*?)<\/sheetData>)/.exec(xml)?.[1] ?? '';
  /** @type {string[][]} */
  const rows = [];
  for (const rowMatch of sheetData.matchAll(/<row\b([^>]*?)(?:\/>|>([\s\S]*?)<\/row>)/g)) {
    /** @type {string[]} */
    const cells = [];
    let nextColumn = 0;
    for (const cellMatch of (rowMatch[2] ?? '').matchAll(/<c\b([^>]*?)(?:\/>|>([\s\S]*?)<\/c>)/g)) {
      const attributes = readAttributes(cellMatch[1]);
      const body = cellMatch[2] ?? '';
      const columnIndex = attributes.r ? columnIndexFromReference(attributes.r) : nextColumn;
      nextColumn = columnIndex + 1;
      if (columnIndex >= MAXIMUM_COLUMNS) continue;
      const rawValue = /<v\b[^>]*>([\s\S]*?)<\/v>/.exec(body)?.[1];
      let value = '';
      switch (attributes.t) {
        case 's':
          value = sharedStrings[Number(rawValue)] ?? '';
          break;
        case 'inlineStr':
          value = readRichText(/<is\b[^>]*>([\s\S]*?)<\/is>/.exec(body)?.[1] ?? '');
          break;
        case 'b':
          value = rawValue === '1' ? 'TRUE' : rawValue === '0' ? 'FALSE' : '';
          break;
        case 'e':
          value = '';
          break;
        case 'str':
        case 'd':
          value = decodeXmlText(rawValue ?? '');
          break;
        default:
          if (rawValue === undefined || rawValue === '') value = '';
          else if (attributes.s !== undefined && dateStyleIndexes.has(Number(attributes.s)) && Number.isFinite(Number(rawValue))) {
            value = excelSerialToIsoText(Number(rawValue), isDate1904);
          } else value = formatNumberCell(decodeXmlText(rawValue));
      }
      while (cells.length < columnIndex) cells.push('');
      cells[columnIndex] = value;
    }
    if (cells.some((cell) => cell.trim() !== '')) rows.push(cells);
  }
  const width = rows.reduce((maximum, row) => Math.max(maximum, row.length), 0);
  for (const row of rows) while (row.length < width) row.push('');
  return rows;
}

/** Caminho do zip a partir do Target do .rels do workbook. @param {string} target */
function resolveWorkbookTarget(target) {
  if (target.startsWith('/')) return target.slice(1);
  const parts = ['xl'];
  for (const part of target.split('/')) {
    if (part === '..') parts.pop();
    else if (part !== '.' && part !== '') parts.push(part);
  }
  return parts.join('/');
}

/**
 * Lê todas as abas visíveis de um XLSX.
 * @param {ArrayBuffer | Uint8Array} input
 * @returns {Promise<SpreadsheetSheet[]>}
 */
export async function readXlsx(input) {
  const bytes = toBytes(input);
  if (!looksLikeZip(bytes)) throw new SpreadsheetReadError('INVALID_SPREADSHEET', 'The file is not a valid XLSX');
  const zip = readZipEntries(bytes);
  const workbook = await readZipText(bytes, zip, 'xl/workbook.xml');
  if (!workbook) throw new SpreadsheetReadError('INVALID_SPREADSHEET', 'The file is not a valid XLSX (workbook not found)');
  const relationships = (await readZipText(bytes, zip, 'xl/_rels/workbook.xml.rels')) ?? '';
  /** @type {Map<string, string>} */
  const targetById = new Map();
  for (const match of relationships.matchAll(/<Relationship\b([^>]*)\/?>/g)) {
    const attributes = readAttributes(match[1]);
    if (attributes.Id && attributes.Target) targetById.set(attributes.Id, resolveWorkbookTarget(attributes.Target));
  }
  const isDate1904 = /<workbookPr\b[^>]*\bdate1904\s*=\s*["'](?:1|true)["']/.test(workbook);
  const sharedStrings = readSharedStrings(await readZipText(bytes, zip, 'xl/sharedStrings.xml'));
  const dateStyleIndexes = readDateStyleIndexes(await readZipText(bytes, zip, 'xl/styles.xml'));
  /** @type {SpreadsheetSheet[]} */
  const sheets = [];
  for (const match of workbook.matchAll(/<sheet\b([^>]*)\/?>/g)) {
    const attributes = readAttributes(match[1]);
    if (attributes.state === 'hidden' || attributes.state === 'veryHidden') continue;
    const relationshipId = attributes['r:id'] ?? Object.entries(attributes).find(([key]) => key.endsWith(':id'))?.[1];
    const path = relationshipId ? targetById.get(relationshipId) : undefined;
    const xml = path ? await readZipText(bytes, zip, path) : null;
    if (xml === null) continue;
    sheets.push({ name: attributes.name ?? `Planilha ${sheets.length + 1}`, rows: readSheetRows(xml, sharedStrings, dateStyleIndexes, isDate1904) });
  }
  if (sheets.length === 0) throw new SpreadsheetReadError('EMPTY_SPREADSHEET', 'The spreadsheet has no readable sheet');
  return sheets;
}
